import React, { useState, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { apiService } from '../../services/api';
import Autocomplete from '../Autocomplete/Autocomplete';
import Toast from '../Toast/Toast';
import './Agendamento.css';

interface AppointmentEvent {
  id: string;
  title: string;
  start: string;
  end: string;
  backgroundColor?: string;
  borderColor?: string;
  textColor?: string;
  display?: string;
}

interface AgendamentoProps {
  user: {
    role: 'paciente' | 'nutricionista';
    name: string;
    email: string;
    id?: string;
  };
}

const Agendamento: React.FC<AgendamentoProps> = ({ user }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [quickBooking, setQuickBooking] = useState({
    date: '',
    time: '',
    patientName: '',
    modalidade: ''
  });
  const [editingEvent, setEditingEvent] = useState<any>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [toast, setToast] = useState<{
    show: boolean;
    message: string;
    type: 'success' | 'warning' | 'danger' | 'info';
  }>({
    show: false,
    message: '',
    type: 'info'
  });



  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = e.target.value;
    setQuickBooking({...quickBooking, date: newDate});
    
    // Navegar calendário para a semana da data selecionada
    if (calendarRef && newDate) {
      const calendarApi = calendarRef.getApi();
      calendarApi.changeView('timeGridWeek', newDate);
    }
  };
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleQuickBooking = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const patientName = user.role === 'paciente' ? user.name : quickBooking.patientName;
    
    if (!quickBooking.date || !quickBooking.time || !quickBooking.modalidade || (user.role === 'nutricionista' && !patientName)) {
      setToast({ show: true, message: 'Preencha todos os campos obrigatórios', type: 'warning' });
      return;
    }

    const startDateTime = new Date(`${quickBooking.date}T${quickBooking.time}:00`);
    const endDateTime = new Date(startDateTime);
    endDateTime.setHours(startDateTime.getHours() + 1);

    // Verificar se horário já está ocupado (considerar sobreposição de horários)
    const isOccupied = events.some(event => {
      const eventStart = new Date(event.start);
      const eventEnd = new Date(event.end);
      
      // Verificar se há sobreposição entre o novo agendamento e eventos existentes
      const hasOverlap = (startDateTime < eventEnd && endDateTime > eventStart);
      
      if (hasOverlap) {
        console.log('Conflito detectado:', {
          novoAgendamento: { start: startDateTime, end: endDateTime },
          eventoExistente: { 
            title: event.title, 
            start: eventStart, 
            end: eventEnd 
          }
        });
      }
      
      return hasOverlap;
    });

    if (isOccupied) {
      setToast({ show: true, message: 'Este horário já está ocupado!', type: 'warning' });
      return;
    }

    try {
      // Salvar no MongoDB
      await apiService.createConsulta({
        pacienteNome: patientName,
        dataHora: startDateTime.toISOString(),
        modalidade: quickBooking.modalidade
      });

      setQuickBooking({ date: '', time: '', patientName: '', modalidade: '' });
      setToast({ show: true, message: 'Agendamento criado com sucesso!', type: 'success' });
      
      // Recarregar todos os eventos
      loadAllEvents();
    } catch (error) {
      console.error('Erro ao criar agendamento:', error);
      setToast({ show: true, message: 'Erro ao criar agendamento. Tente novamente.', type: 'danger' });
    }
  };

  const handleApproveConsulta = async () => {
    if (!editingEvent) return;
    
    try {
      await apiService.approveConsulta(editingEvent.id);
      setToast({ show: true, message: 'Consulta aprovada e enviada para Google Calendar!', type: 'success' });
      
      // Limpar edição e recarregar eventos
      setIsEditing(false);
      setEditingEvent(null);
      setQuickBooking({ date: '', time: '', patientName: '', modalidade: '' });
      loadAllEvents();
    } catch (error) {
      console.error('Erro ao aprovar consulta:', error);
      setToast({ show: true, message: 'Erro ao aprovar consulta. Tente novamente.', type: 'danger' });
    }
  };

  const handleRemoveConsulta = async (consultaId?: string) => {
    const id = consultaId ?? editingEvent?.id;
    if (!id) return;
    
    const title = consultaId ? 'esta consulta' : editingEvent?.title;
    const confirmDelete = window.confirm(`Deseja remover a consulta de ${title}?`);
    if (!confirmDelete) return;
    
    try {
      await apiService.deleteConsulta(id);
      setToast({ show: true, message: 'Consulta removida com sucesso!', type: 'success' });
      
      // Limpar edição e recarregar eventos
      setIsEditing(false);
      setEditingEvent(null);
      setQuickBooking({ date: '', time: '', patientName: '', modalidade: '' });
      loadAllEvents();
    } catch (error) {
      console.error('Erro ao remover consulta:', error);
      setToast({ show: true, message: 'Erro ao remover consulta. Tente novamente.', type: 'danger' });
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingEvent(null);
    setQuickBooking({ date: '', time: '', patientName: '', modalidade: '' });
  };

  // Horários ocupados do Google Calendar
  const [events, setEvents] = useState<AppointmentEvent[]>([]);
  const [calendarRef, setCalendarRef] = useState<any>(null);
  
  useEffect(() => {
    loadAllEvents();
  }, []);
  
  const loadAllEvents = async () => {
    try {
      // Carregar consultas e eventos Google em paralelo
      const [consultas, googleEvents] = await Promise.all([
        apiService.getConsultas().catch(() => []),
        apiService.getGoogleCalendarEvents().catch(() => [])
      ]);
      
      // Formatar consultas
      const consultaEvents: AppointmentEvent[] = consultas.map((consulta: any) => {
        const pacienteNome = consulta.paciente?.nome || consulta.pacienteNome || 'Paciente';
        const isPacienteOwner = user.role === 'paciente' && (
          consulta.paciente?.email === user.email || 
          consulta.paciente?.nome === user.name ||
          consulta.pacienteNome === user.name
        );
        
        // Se for paciente e não for dono da consulta, mostrar como ocupado (cinza)
        if (user.role === 'paciente' && !isPacienteOwner) {
          return {
            id: consulta._id,
            title: 'OCUPADO',
            start: consulta.dataHora,
            end: new Date(new Date(consulta.dataHora).getTime() + 60 * 60 * 1000).toISOString(),
            backgroundColor: '#6c757d',
            borderColor: '#6c757d',
            textColor: '#ffffff',
            extendedProps: {
              isOtherPatient: true
            }
          };
        }
        
        const getPatientDisplayName = (nome: string) => {
          if (user.role !== 'nutricionista') return 'MINHA CONSULTA';
          
          if (isMobile) {
            // Mobile: apenas iniciais no calendário
            return nome.split(' ').map(word => word.charAt(0)).join('').toUpperCase();
          } else {
            // Desktop: apenas primeiro nome no calendário
            return nome.split(' ')[0].toUpperCase();
          }
        };
        
        const getPatientFieldName = (nome: string) => {
          // Só abreviar se o nome for muito longo para a área disponível
          const maxLength = 25;
          
          if (nome.length <= maxLength) {
            return nome; // Nome cabe completo
          }
          
          // Nome muito longo - aplicar abreviação progressiva
          const words = nome.split(' ');
          if (words.length === 1) return nome; // Nome único, manter completo
          
          // Começar com nome completo e abreviar progressivamente
          let result = [...words];
          
          // Abreviar do penúltimo para o segundo nome (manter primeiro e último quando possível)
          for (let i = words.length - 2; i >= 1; i--) {
            const testName = result.join(' ');
            if (testName.length <= maxLength) {
              break; // Já cabe
            }
            result[i] = words[i].charAt(0) + '.'; // Abreviar este sobrenome
          }
          
          return result.join(' ');
        };
        
        return {
          id: consulta._id,
          title: getPatientDisplayName(pacienteNome),
          start: consulta.dataHora,
          end: new Date(new Date(consulta.dataHora).getTime() + 60 * 60 * 1000).toISOString(),
          backgroundColor: consulta.status === 'aprovada' ? '#2d5a3d' : '#f97316',
          borderColor: consulta.status === 'aprovada' ? '#1f4129' : '#ea580c',
          textColor: '#ffffff',
          extendedProps: {
            approved: consulta.status === 'aprovada',
            googleEventId: consulta.googleEventId,
            isPacienteOwner,
            fullPatientName: pacienteNome, // Nome completo original
            fieldPatientName: getPatientFieldName(pacienteNome), // Nome sintetizado para campo
            modalidade: consulta.modalidade ?? 'Presencial' // Modalidade da consulta
          }
        };
      });
      
      // Formatar eventos Google, excluindo os que já existem como consultas aprovadas
      const consultasAprovadas = consultas.filter((c: any) => c.status === 'aprovada' && c.googleEventId);
      const googleEventIds = consultasAprovadas.map((c: any) => c.googleEventId);
      
      const googleFormattedEvents: AppointmentEvent[] = googleEvents
        .filter((event: any) => {
          if (!event.start?.dateTime) {
            return false;
          }
          
          const eventId = event.id.replace('@google.com', '');
          const isExcluded = googleEventIds.includes(eventId);
          
          return !isExcluded;
        })
        .map((event: any, index: number) => {
          const startDate = new Date(event.start.dateTime);
          const endDate = event.end?.dateTime ? new Date(event.end.dateTime) : new Date(startDate.getTime() + 60 * 60 * 1000);
          
          return {
            id: `google-${index}-${startDate.getTime()}`,
            title: `OCUPADO ${index + 1}`,
            start: event.start.dateTime,
            end: event.end?.dateTime || endDate.toISOString(),
            backgroundColor: '#6c757d',
            borderColor: '#6c757d',
            textColor: '#ffffff',
            extendedProps: {
              isGoogleEvent: true,
              originalId: event.id
            }
          };
        });
      
      // Combinar todos os eventos
      const allEvents = [...consultaEvents, ...googleFormattedEvents];
      
      setEvents(allEvents);
      
      // Navegar para dezembro se há eventos Google lá
      if (googleFormattedEvents.length > 0 && calendarRef) {
        const firstGoogleEvent = googleFormattedEvents[0];
        const eventDate = new Date(firstGoogleEvent.start);
        const calendarApi = calendarRef.getApi();
        calendarApi.gotoDate(eventDate);
      }
    } catch (error) {
      console.error('Erro ao carregar eventos:', error);
    }
  };
  
  const handleDateClick = (info: any) => {
    const clickedDate = new Date(info.date);
    const currentView = info.view.type;
    
    // Se estiver na visualização mensal, navegar para o dia
    if (currentView === 'dayGridMonth') {
      const calendarApi = info.view.calendar;
      calendarApi.changeView('timeGridDay', info.dateStr);
      return;
    }
    
    const hour = clickedDate.getHours();
    
    // Só permite agendamentos entre 7:00 e 19:00
    if (hour < 7 || hour >= 19) {
      setToast({ show: true, message: 'Agendamentos disponíveis apenas entre 7:00 e 19:00', type: 'info' });
      return;
    }

    // Preencher campos de data e horário
    const dateStr = clickedDate.toISOString().split('T')[0];
    const timeStr = `${hour.toString().padStart(2, '0')}:00`;
    
    setQuickBooking({
      date: dateStr,
      time: timeStr,
      patientName: isEditing ? '' : quickBooking.patientName, // Limpar paciente se estava editando
      modalidade: isEditing ? '' : quickBooking.modalidade // Limpar modalidade se estava editando
    });

    // Sair do modo de edição se estava editando
    if (isEditing) {
      setIsEditing(false);
      setEditingEvent(null);
    }

    // Se for nutricionista e não tiver nome do paciente, focar no campo
    if (user.role === 'nutricionista' && !quickBooking.patientName) {
      // Scroll para o formulário
      document.querySelector('.card')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleEventClick = (info: any) => {
    const event = info.event;
    
    // Se for evento do Google Calendar (cinza escuro) ou consulta de outro paciente, não editar
    if (event.backgroundColor === '#6c757d' || event.extendedProps?.isOtherPatient) {
      return;
    }
    
    // Para consultas aprovadas (verde escuro) e agendadas (laranja)
    const startDate = new Date(event.start);
    const dateStr = startDate.toISOString().split('T')[0];
    const timeStr = `${startDate.getHours().toString().padStart(2, '0')}:00`;
    
    setQuickBooking({
      date: dateStr,
      time: timeStr,
      patientName: user.role === 'paciente' ? user.name : (event.extendedProps?.fieldPatientName || event.extendedProps?.fullPatientName || event.title),
      modalidade: event.extendedProps?.modalidade ?? ''
    });
    
    setEditingEvent({
      id: event.id,
      title: event.title,
      start: event.start,
      approved: event.backgroundColor === '#2d5a3d' // verde escuro = aprovada
    });
    
    setIsEditing(true);
    
    // Scroll para o formulário
    document.querySelector('.card')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
      />
      
      <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="text-success mb-0">Agendamento</h2>
      </div>
        
        <div className="card shadow-sm p-4 mb-4">
          <h5 className="text-success fw-semibold mb-3">
            {isEditing ? 'Editar Agendamento' : 'Agendamento Rápido'}
          </h5>
          <form onSubmit={isEditing ? (e) => e.preventDefault() : handleQuickBooking}>
            <div className="row g-3">
              <div className="col-md-2">
                <label htmlFor="booking-date" className="form-label fw-medium">Data</label>
                <input
                  id="booking-date"
                  type="date"
                  className="form-control"
                  value={quickBooking.date}
                  onChange={handleDateChange}
                  min={new Date().toISOString().split('T')[0]}
                  required
                  disabled={isEditing}
                />
              </div>
              <div className="col-md-2">
                <label htmlFor="booking-time" className="form-label fw-medium">Horário</label>
                <select
                  id="booking-time"
                  className="form-select"
                  value={quickBooking.time}
                  onChange={(e) => setQuickBooking({...quickBooking, time: e.target.value})}
                  required
                  disabled={isEditing}
                >
                  <option value="">Selecione</option>
                  <option value="07:00">07:00</option>
                  <option value="08:00">08:00</option>
                  <option value="09:00">09:00</option>
                  <option value="10:00">10:00</option>
                  <option value="11:00">11:00</option>
                  <option value="12:00">12:00</option>
                  <option value="13:00">13:00</option>
                  <option value="14:00">14:00</option>
                  <option value="15:00">15:00</option>
                  <option value="16:00">16:00</option>
                  <option value="17:00">17:00</option>
                  <option value="18:00">18:00</option>
                  <option value="19:00">19:00</option>
                </select>
              </div>
              {user.role === 'nutricionista' && (
                <div className="col-md-4">
                  <fieldset className="border-0 p-0 m-0">
                    <legend className="form-label fw-medium fs-6 mb-2">Nome do Paciente</legend>
                    <Autocomplete
                      value={quickBooking.patientName}
                      onChange={(value) => setQuickBooking({...quickBooking, patientName: value})}
                      placeholder="Digite o nome"
                      required
                      readonly={isEditing}
                    />
                  </fieldset>
                </div>
              )}
              <div className="col-md-2">
                <label htmlFor="modalidade" className="form-label fw-medium">Modalidade *</label>
                <select
                  id="modalidade"
                  className="form-select"
                  value={quickBooking.modalidade}
                  onChange={(e) => setQuickBooking({...quickBooking, modalidade: e.target.value})}
                  required
                  disabled={isEditing}
                >
                  <option value="">Selecione</option>
                  <option value="Presencial">Presencial</option>
                  <option value="Remota">Remota</option>
                </select>
              </div>
              <div className="col-md-4 d-flex align-items-end">
                {isEditing ? (
                  <div className="d-flex gap-2 w-100">
                    {user.role === 'nutricionista' ? (
                      // Botões para nutricionista
                      <>
                        {editingEvent?.approved ? (
                          <button type="button" className="btn btn-danger flex-fill" onClick={() => handleRemoveConsulta()}>
                            Remover
                          </button>
                        ) : (
                          <>
                            <button type="button" className="btn btn-success flex-fill" onClick={handleApproveConsulta}>
                              Aprovar
                            </button>
                            <button type="button" className="btn btn-danger flex-fill" onClick={() => handleRemoveConsulta()}>
                              Remover
                            </button>
                          </>
                        )}
                        <button type="button" className="btn btn-secondary flex-fill" onClick={handleCancelEdit}>
                          Cancelar
                        </button>
                      </>
                    ) : (
                      // Botões para paciente
                      <>
                        {!editingEvent?.approved && (
                          <button type="button" className="btn btn-danger flex-fill" onClick={() => handleRemoveConsulta()}>
                            Remover
                          </button>
                        )}
                        <button type="button" className="btn btn-secondary flex-fill" onClick={handleCancelEdit}>
                          Cancelar
                        </button>
                      </>
                    )}
                  </div>
                ) : (
                  <button type="submit" className="btn btn-success w-100">
                    Agendar
                  </button>
                )}
              </div>
            </div>
            
            {/* Mensagem para paciente com consulta aprovada */}
            {user.role === 'paciente' && isEditing && editingEvent?.approved && (
              <div className="mt-2">
                <small className="text-muted">
                  * Para cancelar consulta aprovada, entre em contato com (11) 91111-1111
                </small>
              </div>
            )}
          </form>
        </div>
        
        <div className={`card shadow-sm p-4 ${user.role === 'paciente' ? 'paciente-view' : ''}`}>
          <FullCalendar
          ref={setCalendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView={isMobile ? 'timeGridDay' : 'timeGridWeek'}
          timeZone="local"
          headerToolbar={{
            left: '',
            center: 'prev title next',
            right: 'dayGridMonth,timeGridWeek,timeGridDay'
          }}
          locale="pt-br"
          buttonText={{
            today: 'Hoje',
            month: 'Mês',
            week: 'Semana',
            day: 'Dia',
            prev: '',
            next: ''
          }}
          buttonIcons={{
            prev: 'chevron-left',
            next: 'chevron-right'
          }}
          titleFormat={{
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          }}
          views={{
            timeGridWeek: {
              titleFormat: { day: '2-digit', month: '2-digit' }
            },
            timeGridDay: {
              titleFormat: { day: '2-digit', month: '2-digit' }
            },
            dayGridMonth: {
              titleFormat: { month: 'long', year: 'numeric' }
            }
          }}
          events={events}
          dateClick={handleDateClick}
          eventClick={handleEventClick}
          selectable={true}
          selectMirror={true}
          dayMaxEvents={true}
          weekends={true}
          slotMinTime="07:00:00"
          slotMaxTime="20:00:00"
          slotDuration="01:00:00"
          slotLabelInterval="01:00:00"
          slotLabelFormat={{
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          }}
          height="auto"
          contentHeight={isMobile ? '400px' : '500px'}
          allDaySlot={false}
          nowIndicator={true}
          eventMinHeight={20}
          eventShortHeight={20}
          displayEventTime={true}
          displayEventEnd={true}
          businessHours={{
            daysOfWeek: [1, 2, 3, 4, 5, 6, 0], // Todos os dias
            startTime: '07:00',
            endTime: '20:00'
          }}
          eventDisplay={isMobile ? 'block' : 'auto'}
        />
        </div>
        
        {/* Legenda de cores */}
        <div className="mt-3 d-flex justify-content-center flex-wrap gap-3">
          <div className="d-flex align-items-center gap-2">
            <div style={{width: '16px', height: '16px', backgroundColor: '#6c757d', borderRadius: '4px'}}></div>
            <small className="text-muted">Ocupado</small>
          </div>
          <div className="d-flex align-items-center gap-2">
            <div style={{width: '16px', height: '16px', backgroundColor: '#f97316', borderRadius: '4px'}}></div>
            <small className="text-muted">Aguardando confirmação</small>
          </div>
          <div className="d-flex align-items-center gap-2">
            <div style={{width: '16px', height: '16px', backgroundColor: '#2d5a3d', borderRadius: '4px'}}></div>
            <small className="text-muted">Confirmado</small>
          </div>
        </div>
    </div>
    </>
  );
};

export default Agendamento;